import * as React from "react";
import { Bell, Check, X } from "lucide-react";
import { cn } from "../../lib/cn";
import type { Notification } from "./types";

export interface NotificationCenterProps {
  notifications: Notification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDismiss?: (id: string) => void;
  className?: string;
}

export function NotificationCenter({ notifications, onMarkRead, onMarkAllRead, onDismiss, className }: NotificationCenterProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const unread = notifications.filter((n) => !n.read).length;

  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className={cn("relative", className)} ref={ref}>
      {/* Bell button */}
      <button onClick={() => setOpen((v) => !v)}
        className="relative w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
            {unread > 99 ? "99" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">
              Notifications {unread > 0 && <span className="text-indigo-600">({unread})</span>}
            </p>
            {unread > 0 && (
              <button onClick={onMarkAllRead}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {/* Items */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                You&apos;re all caught up!
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id}
                  className={cn("flex items-start gap-3 px-4 py-3 transition-colors", !n.read && "bg-blue-50/50")}>
                  <div className={cn("flex-shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full", !n.read ? "bg-blue-500" : "bg-transparent")} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 leading-tight">{n.title}</p>
                    {n.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.description}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!n.read && (
                      <button onClick={() => onMarkRead(n.id)}
                        className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        aria-label="Mark as read">
                        <Check className="w-3 h-3" />
                      </button>
                    )}
                    {onDismiss && (
                      <button onClick={() => onDismiss(n.id)}
                        className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        aria-label="Dismiss">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
